import { fromJS } from 'immutable';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AudioWaveOptions from 'blocks/AudioWaveOptions';
import EditorModalsTranscriptionOptions from 'components/EditorModalsTranscriptionOptions';
import {
  ModalBody,
  ModalBodyCol,
  ModalBodyRow,
  ModalFooter,
  ModalFooterButton,
  ModalFooterButtons,
} from 'components/Modal';
import { DEFAULT_LANGUAGE } from 'constants/languages';
import { ConnectedModalInjectedProps } from 'containers/ConnectedModal/ConnectedModalComponent';
import useEditAssetModal from 'containers/ConnectedModal/useEditAssetModal';
import {
  addingToTrack,
  removeFromAudioTrack,
  replaceAudioTrack,
  replaceSelectedTrackElement,
  saveAudioProperties,
} from 'redux/modules/embed/actions';
import { updateAudioCaptions } from 'redux/modules/embed/actions/audio-ts';
import {
  hasTranscriptSelector,
  makeCaptionsEnabledForRecordingByIdSelector,
  selectedAudioAssetSelector,
  selectedTrackElementSelector,
} from 'redux/modules/embed/selectors';
import { onReplaceAsset } from 'redux/modules/mixpanel/actions';
import { pushModal } from 'redux/modules/modal/actions';
import { showError } from 'redux/modules/notification';
import { loadMyPricingData } from 'redux/modules/pricing';
import { Dispatch } from 'redux/types';
import { getValue } from 'utils/collections';
import { millisToSec } from 'utils/time';
import { block } from './utils';
import WaveformModalLink from './WaveformModalLink';

const { useCallback, useState } = React;

export interface EditAudioModalContentsProps {}

const EditAudioModalContents: React.FC<EditAudioModalContentsProps &
  ConnectedModalInjectedProps> = ({ onHide }) => {
  const dispatch = useDispatch<Dispatch>();
  const audio = useSelector(selectedAudioAssetSelector);
  const trackId = useSelector(selectedTrackElementSelector)?.get('trackId');
  const hasTranscript = useSelector(hasTranscriptSelector);

  const { show } = useEditAssetModal('EditAudioModal');

  const [fadeInSec, setFadeInSec] = useState<number>(
    millisToSec(getValue(audio, ['transition', 'in', 'durationMilli'], 0)),
  );
  const [fadeOutSec, setFadeOutSec] = useState<number>(
    millisToSec(getValue(audio, ['transition', 'out', 'durationMilli'], 0)),
  );
  const defaultTranscriptionEnabled = useSelector(
    makeCaptionsEnabledForRecordingByIdSelector(getValue(audio, ['id'], false)),
  );
  const defaultTranscriptionLanguage = getValue(
    audio,
    ['language'],
    DEFAULT_LANGUAGE,
  );
  const clipDurationMillis =
    getValue(audio, 'endMillis', 0) - getValue(audio, 'startMillis', 0);

  const [transcriptionEnabled, setTranscriptionEnabled] = React.useState<
    boolean
  >(defaultTranscriptionEnabled);
  const [transcriptionLanguage, setTranscriptionLanguage] = React.useState<
    string
  >(defaultTranscriptionLanguage);

  React.useEffect(() => {
    if (show) {
      dispatch(loadMyPricingData());
    }
  }, [dispatch, show]);

  const handleDelete = useCallback(() => {
    dispatch(removeFromAudioTrack(audio.get('id')));
    onHide();
  }, [audio, dispatch, onHide]);

  const handleReplace = useCallback(() => {
    dispatch(onReplaceAsset('audio'));
    dispatch(replaceAudioTrack());
    dispatch(replaceSelectedTrackElement());
    onHide();
    dispatch(pushModal({ name: 'AddAudioModal' }));
    dispatch(addingToTrack(trackId));
  }, [dispatch, onHide, trackId]);

  const handleSave = useCallback(async () => {
    dispatch(
      saveAudioProperties(
        audio.withMutations(a => {
          a.set(
            'transition',
            fromJS({
              in: {
                durationMilli: fadeInSec * 1000,
                effect: 'fade',
              },
              out: {
                durationMilli: fadeOutSec * 1000,
                effect: 'fade',
              },
            }),
          );
        }),
      ),
    );

    // Using the promise syntax for not locking the modal while the
    // transcription is being generated.
    dispatch(
      updateAudioCaptions(
        {
          transcriptionEnabled: defaultTranscriptionEnabled,
          transcriptionLanguage: defaultTranscriptionLanguage,
        },
        {
          transcriptionEnabled,
          transcriptionLanguage,
        },
        audio.get('id'),
      ),
    ).catch(() => {
      dispatch(showError('Failed to update the captions'));
    });
    onHide();
  }, [
    audio,
    defaultTranscriptionEnabled,
    defaultTranscriptionLanguage,
    dispatch,
    fadeInSec,
    fadeOutSec,
    onHide,
    transcriptionEnabled,
    transcriptionLanguage,
  ]);

  const hasTranscriptionChanged =
    defaultTranscriptionEnabled !== transcriptionEnabled ||
    defaultTranscriptionLanguage !== transcriptionLanguage;

  return (
    <>
      <ModalBody>
        <ModalBodyRow className={block('row')}>
          <ModalBodyCol size={6} className={block('col', { waveform: true })}>
            <WaveformModalLink onHide={onHide} />
          </ModalBodyCol>
          <ModalBodyCol size={6} className={block('col', { form: true })}>
            <AudioWaveOptions
              audioFadeInDuration={fadeInSec}
              audioFadeOutDuration={fadeOutSec}
              className={block('options')}
              onAudioFadeInChange={setFadeInSec}
              onAudioFadeOutChange={setFadeOutSec}
              transcriptionForm={
                <EditorModalsTranscriptionOptions
                  // Remaining transcription time should not subtract the current duration
                  // if the transcription value has not changed.
                  clipDurationMillis={
                    hasTranscriptionChanged ? clipDurationMillis : 0
                  }
                  hideReplacementWarning={
                    !hasTranscriptionChanged || !hasTranscript
                  }
                  language={transcriptionLanguage}
                  onChangeLanguage={setTranscriptionLanguage}
                  onToggleTranscription={setTranscriptionEnabled}
                  skipUntoggleOnLimitPassed
                  transcriptionEnabled={transcriptionEnabled}
                />
              }
              showAudioFadeControls
              showWaveGenerationField={false}
              showWaveStyleFields={false}
            />
          </ModalBodyCol>
        </ModalBodyRow>
      </ModalBody>
      <ModalFooter>
        <ModalFooterButtons>
          <ModalFooterButton onClick={onHide}>cancel</ModalFooterButton>
          <ModalFooterButton onClick={handleReplace}>replace</ModalFooterButton>
          <ModalFooterButton onClick={handleDelete} theme="delete">
            delete
          </ModalFooterButton>
          <ModalFooterButton onClick={handleSave} theme="submit">
            save
          </ModalFooterButton>
        </ModalFooterButtons>
      </ModalFooter>
    </>
  );
};

export default EditAudioModalContents;
